import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Gauge, Info, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import type { CappingAlias, ObjectiveType } from '@/services/networks/networkFieldSettings'
import {
  DEFAULT_RATE_LIMIT_TYPES,
  RATE_LIMIT_SCOPES,
  newRateLimit,
  type RateLimit,
  type RateLimitScope,
} from '@/services/earnings/rateLimits'
import { CappingRow, type CappingCurrencyControl } from './CappingRow'
import { useCappingText } from './cappingText'

export interface CappingCardProps {
  value: RateLimit[]
  onChange: (limits: RateLimit[]) => void
  /** Fields a cap may be set on — from the network's own settings. */
  types?: ObjectiveType[]
  /** Scope a new cap starts on. */
  defaultScope?: RateLimitScope
  /** What this network calls its caps. */
  alias?: CappingAlias
  /** Drop the line item-wide scope — marketplace-only networks cap per user. */
  perUserOnly?: boolean
  /** Shown instead of the editor, e.g. when a booked product dictates the caps. */
  readOnly?: boolean
  readOnlyReason?: string
  currency?: string
  currencyControl?: CappingCurrencyControl
}

/**
 * The delivery caps on a line item (or anything else the API rate limits):
 * how many impressions, clicks or how much budget it may spend in a period,
 * line item-wide or per individual user.
 */
export function CappingCard({
  value,
  onChange,
  types = DEFAULT_RATE_LIMIT_TYPES,
  defaultScope = 'PER_USER',
  alias = '1',
  perUserOnly,
  readOnly,
  readOnlyReason,
  currency,
  currencyControl,
}: CappingCardProps) {
  const { t } = useTranslation()
  const text = useCappingText(alias)
  // Which rows are open for editing, by identity — indexes shift as rows go.
  const [editing, setEditing] = useState<RateLimit[]>([])

  const scopes: RateLimitScope[] = perUserOnly ? ['PER_USER'] : RATE_LIMIT_SCOPES

  function replace(index: number, limit: RateLimit) {
    const previous = value[index]
    setEditing((open) => open.map((l) => (l === previous ? limit : l)))
    onChange(value.map((l, i) => (i === index ? limit : l)))
  }

  function remove(index: number) {
    const previous = value[index]
    setEditing((open) => open.filter((l) => l !== previous))
    onChange(value.filter((_, i) => i !== index))
  }

  function add() {
    // A field already capped cannot be capped again on the same terms, so lead
    // with one that is still free.
    const used = new Set(value.map((l) => l.type))
    const type = types.find((candidate) => !used.has(candidate)) ?? types[0]
    const limit = newRateLimit(type, perUserOnly ? 'PER_USER' : defaultScope)
    setEditing((open) => [...open, limit])
    onChange([...value, limit])
  }

  return (
    <Card tone="delivery">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CardIcon icon={Gauge} />
          {text.title}
          {value.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">({value.length})</span>
          )}
        </CardTitle>
        {!readOnly && (
          <CardAction>
            <Button type="button" variant="ghost" size="sm" onClick={add}>
              <Plus className="size-3.5" />
              {text.name}
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {value.length === 0 ? (
          <p className="text-sm text-muted-foreground">{text.empty}</p>
        ) : (
          <div className="divide-y divide-dashed">
            {value.map((limit, index) => (
              <CappingRow
                key={index}
                limit={limit}
                onChange={(next) => replace(index, next)}
                onRemove={() => remove(index)}
                // A cap already on an unlisted field keeps its own field on offer.
                types={types.includes(limit.type) ? types : [...types, limit.type]}
                scopes={scopes}
                text={text}
                expanded={editing.includes(limit)}
                onExpandedChange={(open) =>
                  setEditing((current) =>
                    open ? [...current, limit] : current.filter((l) => l !== limit)
                  )
                }
                readOnly={readOnly}
                currency={currency}
                currencyControl={currencyControl}
              />
            ))}
          </div>
        )}

        {readOnly && readOnlyReason && (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            {readOnlyReason}
          </p>
        )}

        {!readOnly && value.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('lineItems.capping.help', { name: text.namePlural.toLowerCase() })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
